
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

/**
 * Combines and merges Tailwind CSS classes efficiently
 * @param inputs - class values to merge
 * @returns Optimized class string
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Formats a currency value
 * @param value - The numeric value to format
 * @param currency - The currency code (default: USD)
 * @returns Formatted currency string
 */
export function formatCurrency(value: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Debounces a function call
 * @param fn - Function to debounce
 * @param ms - Milliseconds to delay
 * @returns Debounced function
 */
export function debounce<T extends (...args: any[]) => any>(fn: T, ms = 300) {
  let timeoutId: ReturnType<typeof setTimeout>;
  
  return function(this: any, ...args: Parameters<T>) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), ms);
  };
}

/**
 * Throttles a function call
 * @param fn - Function to throttle
 * @param ms - Milliseconds to wait between calls
 * @returns Throttled function
 */
export function throttle<T extends (...args: any[]) => any>(fn: T, ms = 300) {
  let canCall = true;
  
  return function(this: any, ...args: Parameters<T>) {
    if (!canCall) return;
    
    canCall = false;
    fn.apply(this, args);
    setTimeout(() => {
      canCall = true;
    }, ms);
  };
}

/**
 * Formats a date into a readable string
 * @param date - Date to format
 * @param format - Format option (default: 'medium')
 * @returns Formatted date string
 */
export function formatDate(date: Date | string, format: 'short' | 'medium' | 'long' = 'medium'): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  const formatOptions: Record<'short' | 'medium' | 'long', Intl.DateTimeFormatOptions> = {
    short: { month: 'numeric', day: 'numeric', year: '2-digit' },
    medium: { month: 'short', day: 'numeric', year: 'numeric' },
    long: { month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }
  };
  
  const options = formatOptions[format];
  return new Intl.DateTimeFormat('en-US', options).format(dateObj);
}

/**
 * Truncates text with ellipsis
 * @param text - Text to truncate
 * @param length - Maximum length (default: 100)
 * @returns Truncated text
 */
export function truncateText(text: string, length = 100): string {
  if (text.length <= length) return text;
  return text.slice(0, length).trim() + '...';
}
